<script setup>
import { ref, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useAddressStore } from '@stores/useAddressStore';
import { trans, transchoice } from '@elements/translation';
import Spinner from '@components/Spinner.vue';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';

const store = useAddressStore();
const { isUploading, allowedImageFormat, images } = storeToRefs(store);
const dropAllowed = ref(false);

const fileUpload = useTemplateRef('fileUpload');

const MAX_AUTHORIZED_IMAGES = 200;
const FILE_SIZE_LIMIT = 20; // max file size on client-side validation, cannot exceed 50mB

const isFileSizeValid = (file) => {
  const fileSizeMb = (file.size / (1024 * 1024));
  return fileSizeMb < FILE_SIZE_LIMIT;
};

const readFile = (file) => new Promise((resolve, reject) => {
  if (!isFileSizeValid(file)) {
    flashBagError((trans('upload.size_exceeded', { max: FILE_SIZE_LIMIT })));
    reject();
    return;
  }

  const reader = new FileReader();

  reader.onload = async (e) => {
    const image = await store.upload({
      file,
      uploading: true,
      src: e.target.result,
    });

    if (image) {
      resolve();
    } else {
      reject();
    }
  };

  reader.readAsDataURL(file);
});

const uploadImages = async (files) => {
  const arrayFiles = Array.from(files);

  if ((arrayFiles.length + images.value.length) > MAX_AUTHORIZED_IMAGES) {
    flashBagError(trans('addresses.details.photos.max_upload_files_reach', { count: MAX_AUTHORIZED_IMAGES }));
    return;
  }

  isUploading.value = true;

  const uploadPromises = arrayFiles.map((file) => readFile(file));
  const results = await Promise.allSettled(uploadPromises);

  const numberOfFailedUpload = results.filter((result) => result.status === 'rejected').length;
  const numberOfSuccessfulUploads = results.filter((result) => result.status === 'fulfilled').length;

  if (numberOfSuccessfulUploads > 0 && !numberOfFailedUpload) {
    flashBagSuccess(
      transchoice('addresses.details.photos.upload_success', { count: numberOfSuccessfulUploads }),
    );
  }

  if (numberOfFailedUpload > 0) {
    flashBagError(
      transchoice('addresses.details.photos.upload_failed', { count: numberOfFailedUpload }),
    );

    images.value = images.value.filter((image) => !image.uploading);
  }

  isUploading.value = false;
};

const triggerUpload = () => {
  fileUpload.value.click();
};

const handleUpload = async (event) => {
  const { files } = event.target;

  await uploadImages(files);
  // eslint-disable-next-line no-param-reassign
  event.target.value = null;
};

const handleDrop = (e) => {
  const { files } = e.dataTransfer;

  dropAllowed.value = false;
  uploadImages(files);
};

let timeout;

const preventEvent = (e) => {
  e.preventDefault();
  dropAllowed.value = true;

  if (timeout) {
    clearTimeout(timeout);
  }
};

const handleDragLeave = (e) => {
  e.preventDefault();
  if (timeout) {
    clearTimeout(timeout);
  }

  timeout = setTimeout(() => {
    dropAllowed.value = false;
  }, 300);
};
</script>

<template>
  <div>
    <div
      class="card card--upload card-upload"
      :class="{ hovered: dropAllowed }"
      @click.prevent="triggerUpload"
      @drop.prevent="handleDrop"
      @dragenter="preventEvent"
    >
      <div
        v-show="dropAllowed"
        class="card-upload__overlay"
        @dragleave="handleDragLeave"
        @dragover="preventEvent"
      >
        <span class="card-upload__icon" />

        <p class="card-upload__label">
          {{ trans('addresses.details.photos.add.drop') }}
        </p>
      </div>

      <div v-show="!dropAllowed" class="card-upload__content">
        <span v-show="!isUploading" class="card-upload__icon" />

        <Spinner v-show="isUploading" />

        <p class="card-upload__label">
          {{ trans('addresses.details.photos.add.label') }}
        </p>

        <a
          href="#"
          class="card-upload__link"
        >
          {{ trans('addresses.details.photos.add.link') }}
        </a>
      </div>
    </div>

    <input
      ref="fileUpload"
      type="file"
      class="card-upload__uploader"
      multiple
      :accept="allowedImageFormat"
      @change="handleUpload"
    >
  </div>
</template>
